import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Table, Card, Select, Typography, Spin, Alert, Space, Button, Row, Col } from 'antd';
import '../App.css';
import { MinusSquareOutlined, PlusSquareOutlined, ReloadOutlined, LeftOutlined, DownOutlined, UpOutlined } from '@ant-design/icons';
import { getFromCacheE, saveExpiryToCache } from '../utils/cacheUtils';
import { useAILimit } from '../user/AIButtonLimitChecker';
import AIUpgradePrompt from '../components/AIUpgradePrompt';
import { API_CONFIG, apiClient } from '../api/config';
import activityTracker from '../user/ActivityTracker';
import { logger } from '../utils/logger';
import { Bar } from 'react-chartjs-2';
import { useCompany } from '../context/CompanyContext';
import { useLocation } from 'react-router-dom';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title as ChartTitle,
  Tooltip,
  Legend,
} from 'chart.js';

const { Option } = Select;
const { Title } = Typography;

// 註冊 Chart.js 組件
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ChartTitle,
  Tooltip,
  Legend
);

// 緩存鍵前綴
const CACHE_KEY_PREFIX = 'cashflow_data_';

const Cashflow = ({ initialSymbol }) => {
  const { companyInfo, updateCompanyInfo } = useCompany();
  const location = useLocation();
  const [cashflowData, setCashflowData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [period, setPeriod] = useState('quarter');
  const [expandedKeys, setExpandedKeys] = useState([]);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisResult, setAnalysisResult] = useState('');
  const [isAnalysisExpanded, setIsAnalysisExpanded] = useState(true);
  const ws = useRef(null);
  const analysisBufferRef = useRef('');
  const { checkAILimit, showUpgradePrompt, setShowUpgradePrompt, limitInfo } = useAILimit();

  // 處理 URL 參數中的 symbol
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const urlSymbol = searchParams.get('symbol');
    
    // 優先使用 URL 參數，其次是 initialSymbol，最後是 companyInfo
    const symbolToUse = urlSymbol || initialSymbol || companyInfo?.symbol;
    
    if (symbolToUse && symbolToUse !== companyInfo?.symbol) {
      logger.log('Cashflow: 從 URL 參數或 initialSymbol 獲取到新的 symbol:', symbolToUse);
      
      // 更新 CompanyContext
      updateCompanyInfo({
        symbol: symbolToUse,
        company_name: '',
        sector: '',
        industry: '',
        image: ''
      });
      
      logger.log('Cashflow: 已更新 symbol 到 CompanyContext');
    }
  }, [location.search, initialSymbol, companyInfo?.symbol, updateCompanyInfo]);

  // 使用 useCallback 包装 fetchCashflowData 函数
  const fetchCashflowData = useCallback(async (symbolToUse, periodToUse, forceRefresh = false) => {
    if (!symbolToUse) {
              logger.log('沒有提供股票代碼，顯示空狀態');
      setLoading(false);
      return;
    }
    
    const cacheKey = `${CACHE_KEY_PREFIX}${symbolToUse}_${periodToUse}`;
    
    if (!forceRefresh) {
      const cachedData = getFromCacheE(cacheKey);
      if (cachedData) {
        logger.log(`從緩存載入現金流量表數據: ${cacheKey}`);
        setCashflowData(cachedData);
        setLoading(false);
        return;
      }
    }
    
    setLoading(true);
    setError(null);
    try {
      const endpoint = API_CONFIG.FINANCIAL.CASHFLOW(symbolToUse, periodToUse);
      const response = await apiClient.get(endpoint);
      logger.log('API響應成功，數據長度:', response.data.length);
      
      if (response.data.length === 0) {
        setError('API返回了空數據');
        return;
      }
      
      setCashflowData(response.data);
      saveExpiryToCache(cacheKey, response.data, 0.5);
      
    } catch (err) {
      logger.error('獲取現金流量表數據失敗，錯誤詳情:', err);
      setError(`無法載入現金流量表資料: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  // 当公司信息或期间变化时获取数据
  useEffect(() => {
    if (companyInfo.symbol) {
      fetchCashflowData(companyInfo.symbol, period);
      activityTracker.trackPageView(companyInfo.symbol);
    }
  }, [companyInfo.symbol, period, fetchCashflowData]);

  const handleRefreshData = () => {
    if (companyInfo.symbol) {
      fetchCashflowData(companyInfo.symbol, period, true);
    }
  };

  const handlePeriodChange = (value) => {
    setPeriod(value);
  };

  const handleExpand = (expanded, record, e) => {
    e.stopPropagation();
    
    if (expanded) {
      setExpandedKeys([...expandedKeys, record.key]);
    } else {
      setExpandedKeys(expandedKeys.filter(k => k !== record.key));
    }
  };

  // Markdown 轉 HTML（參考 list_10q10k.js）
  const convertMarkdownToHtml = (markdownText) => {
    if (!markdownText) return '';
    let html = markdownText;

    // 表格
    const tableRegex = /(\|[^\n]+\|\n\|[\s\-:|]+\|\n(\|[^\n]+\|\n?)+)/g;
    html = html.replace(tableRegex, (match) => {
      const lines = match.trim().split('\n');
      if (lines.length < 3) return match;
      let tableHtml = '<table style="border-collapse: collapse; width: 100%; margin: 10px 0; border: 1px solid #ddd; font-size: 13px;">';
      lines.forEach((line, index) => {
        if (index === 1) return; // 跳過分隔行
        const cells = line.split('|').filter(cell => cell.trim() !== '');
        if (cells.length === 0) return;
        const tag = index === 0 ? 'th' : 'td';
        tableHtml += '<tr>';
        cells.forEach((cell, cellIndex) => {
          const content = cell.trim();
          let style = '';
          if (index === 0) {
            style = 'background-color: #f2f2f2; font-weight: bold; padding: 6px 4px; border: 1px solid #ddd; text-align: center; font-size: 12px;';
          } else {
            style = 'padding: 6px 4px; border: 1px solid #ddd; text-align: left; font-size: 12px;';
            if (cellIndex === 0) {
              style += ' text-align: left;';
            } else {
              style += ' text-align: center;';
            }
          }
          tableHtml += `<${tag} style="${style}">${content}</${tag}>`;
        });
        tableHtml += '</tr>';
      });
      tableHtml += '</table>';
      return tableHtml;
    });

    // 粗體、斜體
    html = html.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    html = html.replace(/__(.*?)__/g, '<strong>$1</strong>');
    html = html.replace(/\*(.*?)\*/g, '<em>$1</em>');
    html = html.replace(/_(.*?)_/g, '<em>$1</em>');

    // 標題
    html = html.replace(/^#### (.*$)/gim, '<h4 style="margin: 12px 0 6px 0; color: #1890ff; font-size: 16px;">$1</h4>');
    html = html.replace(/^### (.*$)/gim, '<h3 style="margin: 16px 0 8px 0; color: #1890ff; font-size: 18px;">$1</h3>');
    html = html.replace(/^## (.*$)/gim, '<h2 style="margin: 20px 0 12px 0; color: #1890ff; font-size: 20px;">$1</h2>');
    html = html.replace(/^# (.*$)/gim, '<h1 style="margin: 24px 0 16px 0; color: #1890ff; font-size: 22px;">$1</h1>');

    // 列表
    html = html.replace(/^\* (.*$)/gim, '<li style="margin: 4px 0;">$1</li>');
    html = html.replace(/^- (.*$)/gim, '<li style="margin: 4px 0;">$1</li>');

    // 段落
    html = html.replace(/\n\n/g, '</p><p style="margin: 8px 0;">');
    html = html.replace(/^(?!<[h|t|u|o|li])(.*$)/gim, '<p style="margin: 8px 0;">$1</p>');
    html = html.replace(/<p><\/p>/g, '');
    html = html.replace(/<p style="margin: 8px 0;"><\/p>/g, '');

    return html;
  };

  // 將數據轉換為每個財年或季度作為一列，並添加可展開功能
  const transformData = () => {
    if (!cashflowData || cashflowData.length === 0) return [];

    // 獲取所有列名（除了不需要顯示的列）
    const excludeFields = ['symbol', 'fiscalYear', 'period'];
    const allFields = Object.keys(cashflowData[0]).filter(field => !excludeFields.includes(field));

    // 定義欄位的自定義順序和分組關係
    const customOrder = [
      'date',

      // 經營活動產生的現金流量
      'netCashProvidedByOperatingActivities',  // 父項目
      'growthNetCashProvidedByOperatingActivitesPercent',
      'netIncome',
      'depreciationAndAmortization',
      'deferredIncomeTax',
      'stockBasedCompensation',
      'growthStockBasedCompensationPercent',
      'changeInWorkingCapital',
      'accountsReceivables',
      'inventory',
      'growthInventoryPercent',
      'accountsPayables',
      'otherWorkingCapital',
      'otherNonCashItems',

      // 投資活動產生的現金流量
      'netCashProvidedByInvestingActivities',  // 父項目
      'growthNetCashUsedForInvestingActivitesPercent',
      'investmentsInPropertyPlantAndEquipment',
      'acquisitionsNet',
      'purchasesOfInvestments',
      'salesMaturitiesOfInvestments',
      'otherInvestingActivites',
      'otherInvestingActivities',  // 新增：其他投資活動（另一種拼寫）

      // 融資活動產生的現金流量
      'netCashProvidedByFinancingActivities',  // 父項目
      'growthNetCashUsedProvidedByFinancingActivitiesPercent',
      'netDebtIssuance',  // 新增：債務發行淨額
      'longTermNetDebtIssuance',  // 新增：長期債務發行淨額
      'shortTermNetDebtIssuance',  // 新增：短期債務發行淨額
      'debtRepayment',
      'netStockIssuance',  // 新增：股票發行淨額
      'netCommonStockIssuance',  // 新增：普通股發行淨額
      'commonStockIssued',
      'commonStockIssuance',  // 新增：普通股發行（另一種表達）
      'netPreferredStockIssuance',  // 新增：優先股發行淨額
      'commonStockRepurchased',
      'growthCommonStockRepurchasedPercent',
      'netDividendsPaid',  // 新增：股息支付淨額
      'dividendsPaid',
      'commonDividendsPaid',  // 新增：普通股股息支付
      'preferredDividendsPaid',  // 新增：優先股股息支付
      'otherFinancingActivites',
      'otherFinancingActivities',  // 新增：其他融資活動（另一種拼寫）

      // 現金變動
      'netChangeInCash',  // 父項目
      'cashAtBeginningOfPeriod',
      'cashAtEndOfPeriod',

      // 自由現金流相關
      'freeCashFlow',
      'growthFreeCashFlowPercent'  // 新增：自由現金流增長率百分比
    ];

    // 定義父子項目關係
    const groupMap = {
      'netCashProvidedByOperatingActivities': [
        'growthNetCashProvidedByOperatingActivitesPercent',
        'netIncome',
        'depreciationAndAmortization',
        'deferredIncomeTax',
        'stockBasedCompensation',
        'growthStockBasedCompensationPercent',
        'changeInWorkingCapital',
        'accountsReceivables',
        'inventory',
        'growthInventoryPercent',
        'accountsPayables',
        'otherWorkingCapital',
        'otherNonCashItems'
      ],
      'netCashProvidedByInvestingActivities': [
        'growthNetCashUsedForInvestingActivitesPercent',
        'investmentsInPropertyPlantAndEquipment',
        'acquisitionsNet',
        'purchasesOfInvestments',
        'salesMaturitiesOfInvestments',
        'otherInvestingActivites',
        'otherInvestingActivities'  // 新增
      ],
      'netCashProvidedByFinancingActivities': [
        'growthNetCashUsedProvidedByFinancingActivitiesPercent',
        'netDebtIssuance',  // 新增
        'longTermNetDebtIssuance',  // 新增
        'shortTermNetDebtIssuance',  // 新增
        'debtRepayment',
        'netStockIssuance',  // 新增
        'netCommonStockIssuance',  // 新增
        'commonStockIssued',
        'commonStockIssuance',  // 新增
        'netPreferredStockIssuance',  // 新增
        'commonStockRepurchased',
        'growthCommonStockRepurchasedPercent',
        'netDividendsPaid',  // 新增
        'dividendsPaid',
        'commonDividendsPaid',  // 新增
        'preferredDividendsPaid',  // 新增
        'otherFinancingActivites',
        'otherFinancingActivities'  // 新增
      ],
      'netChangeInCash': [
        'cashAtBeginningOfPeriod',
        'cashAtEndOfPeriod'
      ],
      'freeCashFlow': [
        'growthFreeCashFlowPercent'  // 新增
      ]
    };

    // 收集所有子項目
    const allChildren = Object.values(groupMap).flat();

    // 按自定義順序排序欄位
    const sortedFields = [...allFields].sort((a, b) => {
      const indexA = customOrder.indexOf(a);
      const indexB = customOrder.indexOf(b);

      // 如果欄位不在自定義順序中，放到最後
      if (indexA === -1) return 1;
      if (indexB === -1) return -1;

      return indexA - indexB;
    });

    // 創建一個映射，存儲每個欄位的數據行
    const rowsMap = {};

    // 提前計算所有列標識，避免重複計算
    const columnKeys = [];
    cashflowData.forEach(data => {
      columnKeys.push({
        data: data,
        key: period === 'quarter' 
          ? `${data.fiscalYear}${data.period}`
          : data.fiscalYear
      });
    });

    // 輔助函數：填充行數據
    const fillRowData = (rowData, field) => {
      columnKeys.forEach(({data, key}) => {
        if (data[field] !== undefined) {
          rowData[key] = data[field];
        } else {
          rowData[key] = '';
        }
      });
      return rowData;
    };

    // 先創建所有父項目的行
    sortedFields.forEach(field => {
      // 如果字段是父項目或不是任何父項目的子項目
      if (!allChildren.includes(field)) {
        const rowData = {
          key: field,
          field: field
        };

        fillRowData(rowData, field);
        rowsMap[field] = rowData;
      }
    });

    // 然後為每個父項目添加子項目
    Object.entries(groupMap).forEach(([parent, children]) => {
      // 確保父項目存在
      if (!rowsMap[parent]) return;

      // 如果父項目存在，則添加子項目
      const childrenData = [];

      children.forEach(childField => {
        // 確保字段存在於數據中
        if (!sortedFields.includes(childField)) return;

        const childRowData = {
          key: childField,
          field: childField,
          parent: parent  // 記錄父項目
        };

        fillRowData(childRowData, childField);
        rowsMap[childField] = childRowData;
        childrenData.push(childRowData);
      });

      // 為父項目添加子項目
      if (childrenData.length > 0) {
        rowsMap[parent].children = childrenData;
      }
    });

    // 將映射轉換回數組，確保順序正確
    const result = sortedFields
      .filter(field => !allChildren.includes(field))
      .map(field => rowsMap[field]);
    
    return result;
  };

  // 定義需要顯示為深藍色的欄位
  const blueTextFields = ['netCashProvidedByOperatingActivities', 'netCashProvidedByInvestingActivities', 
                         'netCashProvidedByFinancingActivities'];
  

  // 生成表格列配置
  const generateColumns = () => {
    if (!cashflowData || cashflowData.length === 0) return [];

    // 創建一個集合來存儲唯一的列標識
    const columnKeys = new Set();
    const columns = [
      {
        title: '財務年(季)度',
        dataIndex: 'field',
        key: 'field',
        width: 120,
        fixed: 'left',
        render: (value, record) => {
          // 美化字段名顯示
          const fieldLabels = {
            'date': '發布日期',

            // 經營活動現金流
            'netCashProvidedByOperatingActivities': '經營活動現金流',
            'growthNetCashProvidedByOperatingActivitesPercent': '經營活動現金流YoY',
            'netIncome': '淨利',
            'depreciationAndAmortization': '折舊和攤銷',
            'deferredIncomeTax': '遞延所得稅',
            'stockBasedCompensation': '股票薪酬',
            'growthStockBasedCompensationPercent': '股票薪酬YoY',
            'changeInWorkingCapital': '營運資金變動',
            'accountsReceivables': '應收賬款變動',
            'inventory': '存貨變動',
            'growthInventoryPercent': '存貨變動YoY',
            'accountsPayables': '應付賬款變動',
            'otherWorkingCapital': '其他營運資金變動',
            'otherNonCashItems': '其他非現金項目',

            // 投資活動現金流
            'netCashProvidedByInvestingActivities': '投資活動現金流',
            'growthNetCashUsedForInvestingActivitesPercent': '投資活動現金流YoY',
            'investmentsInPropertyPlantAndEquipment': '不動產、廠房及設備投資',
            'acquisitionsNet': '收購淨額',
            'purchasesOfInvestments': '投資購買',
            'salesMaturitiesOfInvestments': '投資銷售及到期',
            'otherInvestingActivites': '其他投資活動',
            'otherInvestingActivities': '其他投資活動',  // 新增

            // 融資活動現金流
            'netCashProvidedByFinancingActivities': '融資活動現金流',
            'growthNetCashUsedProvidedByFinancingActivitiesPercent': '融資活動現金流YoY',
            'netDebtIssuance': '債務發行淨額',  // 新增
            'longTermNetDebtIssuance': '長期債務發行淨額',  // 新增
            'shortTermNetDebtIssuance': '短期債務發行淨額',  // 新增
            'debtRepayment': '債務償還',
            'netStockIssuance': '股票發行淨額',  // 新增
            'netCommonStockIssuance': '普通股發行淨額',  // 新增
            'commonStockIssued': '普通股發行',
            'commonStockIssuance': '普通股發行',  // 新增
            'netPreferredStockIssuance': '優先股發行淨額',  // 新增
            'commonStockRepurchased': '普通股回購',
            'growthCommonStockRepurchasedPercent': '普通股回購YoY',
            'netDividendsPaid': '股息支付淨額',  // 新增
            'dividendsPaid': '股息支付',
            'commonDividendsPaid': '普通股股息支付',  // 新增
            'preferredDividendsPaid': '優先股股息支付',  // 新增
            'otherFinancingActivites': '其他融資活動',
            'otherFinancingActivities': '其他融資活動',  // 新增

            // 現金變動
            'netChangeInCash': '現金淨變動',
            'cashAtBeginningOfPeriod': '期初現金',
            'cashAtEndOfPeriod': '期末現金',

            // 自由現金流
            'freeCashFlow': '自由現金流',
            'growthFreeCashFlowPercent': '自由現金流YoY'  // 新增
          };
          const displayName = fieldLabels[value] || value;

          // 設置不同類型欄位的樣式
          let textStyle = {};
          let className = '';

          // 一般的合計項目使用深藍色粗體
          if (blueTextFields.includes(value)) {
            textStyle = { color: '#1a2952', fontWeight: 'bold' };
          }

          // 如果有子項目，添加展開/縮放圖標
          if (record.children && record.children.length > 0) {
            return (
              <span className="expandable-row-label">
                <span className={className} style={textStyle}>{displayName}</span>
                <span className="expand-icon" onClick={(e) => {
                  const expanded = expandedKeys.includes(record.key);
                  handleExpand(!expanded, record, e);
                  e.stopPropagation();
                }}>
                  {expandedKeys.includes(record.key) ? 
                    <MinusSquareOutlined style={{ marginLeft: 8 }} /> : 
                    <PlusSquareOutlined style={{ marginLeft: 8 }} />
                  }
                </span>
              </span>
            );
          }

          return <span className={className} style={textStyle}>{displayName}</span>;
        }
      }
    ];

    // 為每個財年/季度的數據添加列定義
    cashflowData.forEach(data => {
      // 根據 period 參數決定如何創建列標識
      const columnKey = period === 'quarter' 
        ? `${data.fiscalYear}${data.period}` // 季度數據
        : data.fiscalYear;                    // 年度數據

      // 避免重複列
      if (!columnKeys.has(columnKey)) {
        columnKeys.add(columnKey);

        columns.push({
          title: columnKey,
          dataIndex: columnKey,
          key: columnKey,
          align: 'right', // 所有數據列默認右對齊
          width: 100,
          render: (text, record) => {
            // 對於成長率欄位，根據正負值設置顏色
            if (record.field.includes('growth')) {
              // 檢查是否包含負號（如 "-2.5 %"）
              const isNegative = text && text.includes('-');
              
              return (
                <span style={{ 
                  color: isNegative ? '#ff4d4f' : '#52c41a',  // 負數紅色，正數綠色
                  fontWeight: 'bold'
                }}>
                  {text}
                </span>
              );
            }

            // 對於合計欄位，設置深藍色
            if (blueTextFields.includes(record.field)) {
              return (
                <span style={{ 
                  color: '#1a2952',  // 深藍色 
                  fontWeight: 'bold'
                }}>
                  {text}
                </span>
              );
            }

            // 其他欄位維持原樣
            return text;
          }
        });
      }
    });

    // 確保列按時間順序排序（最新的在左側）
    columns.sort((a, b) => {
      if (a.dataIndex === 'field') return -1; // 項目列始終在最左側
      if (b.dataIndex === 'field') return 1;

      // 直接比較字符串表示
      return String(b.dataIndex) > String(a.dataIndex) ? 1 : -1;
    });

    return columns;
  };

  const handleAnalyze = async () => {
    if (!companyInfo.symbol) return;
    // AI 使用次數檢查
    const limitCheck = await checkAILimit('AI-cashflow');
    if (!limitCheck.canProceed) {
      setAnalysisResult(limitCheck.reason || 'AI 使用次數已達上限');
      return;
    }

    setIsAnalyzing(true);
    setAnalysisResult('');
    analysisBufferRef.current = '';
    
    const wsProtocol = window.location.protocol === 'https:' ? 'wss' : 'ws';
    const wsHost = window.location.hostname === 'localhost'
      ? 'localhost:5080'
      : window.location.host;
    const wsUrl = `${wsProtocol}://${wsHost}/ws-app`;

    if (!ws.current || ws.current.readyState !== WebSocket.OPEN) {
      ws.current = new WebSocket(wsUrl);
      
      ws.current.onopen = () => {
        ws.current.send(JSON.stringify({
          type: 'cashflow-analysis',
          symbol: companyInfo.symbol
        }));
      };
      
      ws.current.onmessage = (event) => {
        const data = JSON.parse(event.data);
        if (data.type === 'cashflow') {
          analysisBufferRef.current += data.content || '';
          const html = convertMarkdownToHtml(analysisBufferRef.current);
          setAnalysisResult(html);
        } else if (data.type === 'complete') {
          try {
            const cacheKey = `ai_cashflow_analysis_${companyInfo.symbol}`;
            const finalHtml = convertMarkdownToHtml(analysisBufferRef.current);
            saveExpiryToCache(cacheKey, {
              symbol: companyInfo.symbol,
              content: finalHtml,
              updatedAt: Date.now()
            }, 90);
          } catch (e) {}
          setIsAnalyzing(false);
        } else if (data.error) {
          setAnalysisResult(data.error);
          setIsAnalyzing(false);
        }
      };
      
      ws.current.onerror = (error) => {
        console.error('WebSocket error:', error);
        setAnalysisResult('分析過程中發生錯誤');
        setIsAnalyzing(false);
      };
    } else {
      ws.current.send(JSON.stringify({
        type: 'cashflow-analysis',
        symbol: companyInfo.symbol
      }));
    }

    activityTracker.trackBtnExecution('AI-cashflow', companyInfo.symbol, window.location.pathname);
  };

  // 处理带单位的数据
  const parseValueWithUnit = (value) => {
    if (typeof value === 'string') {
      // 移除单位（M、B等）和空格
      return parseFloat(value.replace(/[MBK\s]/g, '').trim());
    }
    return value;
  };

  // 准备图表数据
  const prepareChartData = () => {
    if (!cashflowData || cashflowData.length === 0) return null;

    // 按时间倒序排序数据（最新的在前）
    const sortedData = [...cashflowData].sort((a, b) => {
      const dateA = new Date(a.date);
      const dateB = new Date(b.date);
      return dateB - dateA;
    });

    // 只取最近20条数据
    const recentData = sortedData.slice(0, 20);

    // 准备标签（日期）
    const labels = recentData.map(item => {
      const date = new Date(item.date);
      return period === 'quarter' 
        ? `${date.getFullYear()}${item.period}`
        : `${date.getFullYear()}`;
    });

    // 准备第一个 Bar Chart 数据（不动产投资、股票回购、股票薪酬）
    const barData1 = {
      labels,
      datasets: [
        {
          label: '不動產、廠房及設備投資',
          data: recentData.map(item => Math.abs(parseValueWithUnit(item.investmentsInPropertyPlantAndEquipment))),
          backgroundColor: 'rgba(54, 162, 235, 0.5)',
          borderColor: 'rgba(54, 162, 235, 1)',
          borderWidth: 1,
        },
        {
          label: '普通股回購',
          data: recentData.map(item => Math.abs(parseValueWithUnit(item.commonStockRepurchased))),
          backgroundColor: 'rgba(255, 99, 132, 0.5)',
          borderColor: 'rgba(255, 99, 132, 1)',
          borderWidth: 1,
        },
        {
          label: '股票薪酬',
          data: recentData.map(item => parseValueWithUnit(item.stockBasedCompensation)),
          backgroundColor: 'rgba(75, 192, 192, 0.5)',
          borderColor: 'rgba(75, 192, 192, 1)',
          borderWidth: 1,
        }
      ],
    };

    // 准备第二个 Bar Chart 数据（自由现金流、净利）
    const barData2 = {
      labels,
      datasets: [
        {
          label: '自由現金流',
          data: recentData.map(item => parseValueWithUnit(item.freeCashFlow)),
          backgroundColor: 'rgba(153, 102, 255, 0.5)',
          borderColor: 'rgba(153, 102, 255, 1)',
          borderWidth: 1,
        },
        {
          label: '淨利',
          data: recentData.map(item => parseValueWithUnit(item.netIncome)),
          backgroundColor: 'rgba(255, 159, 64, 0.5)',
          borderColor: 'rgba(255, 159, 64, 1)',
          borderWidth: 1,
        }
      ],
    };

    return { barData1, barData2 };
  };

  // 嘗試載入AI分析緩存
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const symbolFromUrl = urlParams.get('symbol') || '';
    const symbol = symbolFromUrl || companyInfo.symbol;
    if (!symbol) return;
    try {
      const cached = getFromCacheE(`ai_cashflow_analysis_${symbol}`);
      if (cached && cached.content) {
        setAnalysisResult(cached.content);
      }
    } catch (e) {}
  }, []);

  // 图表配置
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            let label = context.dataset.label || '';
            if (label) {
              label += ': ';
            }
            if (context.parsed.y !== null) {
              label += new Intl.NumberFormat('zh-TW', {
                style: 'decimal',
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              }).format(context.parsed.y) + ' M';
            }
            return label;
          }
        }
      }
    },
    scales: {
      x: {
        ticks: {
          maxRotation: 45,
          minRotation: 45
        },
        grid: {
          display: false
        }
      },
      y: {
        beginAtZero: true,
        ticks: {
          callback: function(value) {
            return new Intl.NumberFormat('zh-TW', {
              style: 'decimal',
              minimumFractionDigits: 2,
              maximumFractionDigits: 2
            }).format(value) + ' M';
          }
        }
      }
    },
    barPercentage: 0.8,
    categoryPercentage: 0.9
  };

  return (
    <div className="container mt-4">
      <Card
        title={
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
            <div className="card-title">
              <Button 
                type="primary"
                onClick={handleAnalyze}
                loading={isAnalyzing}
                icon={<ReloadOutlined />}
                style={{ backgroundColor: '#1890ff', borderColor: '#1890ff', fontWeight: 'bold' }}
              >
                {isAnalyzing ? '分析中...' : '分析最新季度'}
              </Button>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <Select 
                defaultValue="quarter"
                style={{ width: 120 }} 
                onChange={handlePeriodChange}
                loading={loading}
              >
                <Option value="FY">年度</Option>
                <Option value="quarter">季度</Option>
              </Select>
              <Button onClick={handleRefreshData} style={{ marginLeft: 0 }}>刷新數據</Button>
            </div>
          </div>
        } 
        className="financial-card"
      >
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          {analysisResult && (
            <Card className="mb-4">
              <div 
                style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  cursor: 'pointer'
                }}
                onClick={() => setIsAnalysisExpanded(!isAnalysisExpanded)}
              >
                <Title level={4} style={{ margin: 0 }}>分析結果</Title>
                {isAnalysisExpanded ? <UpOutlined /> : <DownOutlined />}
              </div>
              {isAnalysisExpanded && (
                <div 
                  className="ai-analysis-html"
                  style={{ lineHeight: '1.6', fontSize: '14px', marginTop: '16px' }}
                  dangerouslySetInnerHTML={{ __html: analysisResult }}
                />
              )}
            </Card>
          )}
          {error && <Alert type="error" message={error} />}

          {loading ? (
            <div style={{ textAlign: 'center', margin: '50px 0' }}>
              <Spin size="large" />
            </div>
          ) : (
            <>
              {/* 添加图表 */}
              {cashflowData && cashflowData.length > 0 && (
                <div style={{ marginTop: '10px', marginBottom: '20px' }}>
                  <Row gutter={[16, 16]}>
                    <Col xs={24} lg={12}>
                      <Card title="投資與回購趨勢" className="mb-4">
                        <div style={{ height: '300px', overflowX: 'auto' }}>
                          <div style={{ minWidth: '600px', height: '100%' }}>
                            <Bar data={prepareChartData()?.barData1} options={chartOptions} />
                          </div>
                        </div>
                        {/* 添加时间提示 */}
                        <div style={{ 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          alignItems: 'center',
                          marginTop: '5px', 
                          padding: '0 15px',
                          fontSize: '14px',
                          color: '#333'
                        }}>
                          <span style={{ fontWeight: 'bold' }}>近期</span>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                            <LeftOutlined style={{ fontSize: '18px', color: '#1890ff', fontWeight: 'bold' }} />
                            <LeftOutlined style={{ fontSize: '18px', color: '#1890ff', fontWeight: 'bold' }} />
                            <LeftOutlined style={{ fontSize: '18px', color: '#1890ff', fontWeight: 'bold' }} />
                          </div>
                          <span style={{ fontWeight: 'bold' }}>遠期</span>
                        </div>
                      </Card>
                    </Col>
                    <Col xs={24} lg={12}>
                      <Card title="現金流與淨利趨勢">
                        <div style={{ height: '300px', overflowX: 'auto' }}>
                          <div style={{ minWidth: '600px', height: '100%' }}>
                            <Bar data={prepareChartData()?.barData2} options={chartOptions} />
                          </div>
                        </div>
                        {/* 添加时间提示 */}
                        <div style={{ 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          alignItems: 'center',
                          marginTop: '5px', 
                          padding: '0 15px',
                          fontSize: '14px',
                          color: '#333'
                        }}>
                          <span style={{ fontWeight: 'bold' }}>近期</span>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                            <LeftOutlined style={{ fontSize: '18px', color: '#1890ff', fontWeight: 'bold' }} />
                            <LeftOutlined style={{ fontSize: '18px', color: '#1890ff', fontWeight: 'bold' }} />
                            <LeftOutlined style={{ fontSize: '18px', color: '#1890ff', fontWeight: 'bold' }} />
                          </div>
                          <span style={{ fontWeight: 'bold' }}>遠期</span>
                        </div>
                      </Card>
                    </Col>
                  </Row>
                </div>
              )}

              <Table 
                columns={generateColumns()} 
                dataSource={transformData()} 
                rowKey="field"
                pagination={false}
                scroll={{ x: 'max-content' }} 
                size="middle"
                tableLayout="fixed"
                bordered
                expandedRowKeys={expandedKeys}
                onExpand={(expanded, record) => {
                  handleExpand(expanded, record, { stopPropagation: () => {} });
                }}
                expandable={{
                  expandIcon: () => null, // 隱藏默認的展開圖標
                  expandRowByClick: true
                }}
                onRow={(record) => {
                  if (record.field === 'date') {
                    return { style: { backgroundColor: '#FFFBE6' } };  // 淺黃色背景
                  }
                  return {};
                }}
              />
            </>
          )}
        </Space>

        <AIUpgradePrompt
          visible={showUpgradePrompt}
          onCancel={() => setShowUpgradePrompt(false)}
          limitInfo={limitInfo}
        />

        <style jsx>{`
          .financial-card {
            margin-bottom: 20px;
          }
          .expandable-row-label {
            display: flex;
            align-items: center;
            justify-content: space-between;
          }
          .expand-icon {
            cursor: pointer;
          }
          .ant-table {
            font-size: 14px;
          }
          .ant-table-thead > tr > th {
            font-size: 14px;
            padding: 8px;
          }
          .ant-table-tbody > tr > td {
            font-size: 14px;
            padding: 8px;
          }
          .ant-card-head-title {
            font-size: 16px;
          }
          .ant-select {
            font-size: 14px;
          }
          .ant-select-selector {
            font-size: 14px !important;
          }
          .ant-btn {
            font-size: 14px;
          }
          .ant-btn[style*="background-color: #ffffff"]:hover {
            background: #f5f5f5 !important;
            border-color: #bfbfbf !important;
            color: #000000 !important;
          }

          @media screen and (max-width: 768px) {
            .ant-table {
              font-size: 11px;
            }
            .ant-table-thead > tr > th {
              font-size: 11px;
              padding: 4px;
            }
            .ant-table-tbody > tr > td {
              font-size: 11px;
              padding: 4px;
            }
            .ant-card-head-title {
              font-size: 14px;
            }
            .ant-select {
              font-size: 11px;
            }
            .ant-select-selector {
              font-size: 11px !important;
            }
            .ant-btn {
              font-size: 11px;
            }
          }

          @media screen and (max-width: 480px) {
            .ant-table {
              font-size: 12px;
            }
            .ant-table-thead > tr > th {
              font-size: 12px;
              padding: 1px;
            }
            .ant-table-tbody > tr > td {
              font-size: 12px;
              padding: 1px;
            }
            .ant-card-head-title {
              font-size: 14px;
            }
            .ant-select {
              font-size: 12px;
            }
            .ant-select-selector {
              font-size: 12px !important;
            }
            .ant-btn {
              font-size: 12px;
            }
          }
        `}</style>
      </Card>
    </div>
  );
};

export default Cashflow; 